<template>
  <div class="bandeja">
    <!-- El subtítulo de la maqueta: de quién son las solicitudes que se van a ver. Un ADMIN o un
         DIRECTOR aprueba a toda la plantilla (D3), y escribir ahí cien nombres no diría nada. -->
    <p class="bandeja-subtitulo">{{ subtitulo }}</p>

    <f7-block strong class="tabla-wrapper">
      <table class="tabla-vacaciones">
        <thead>
          <tr>
            <th>{{ $t('bandejaVacaciones.persona') }}</th>
            <th>{{ $t('bandejaVacaciones.fechas') }}</th>
            <th class="col-centro">{{ $t('bandejaVacaciones.dias') }}</th>
            <th>{{ $t('bandejaVacaciones.solicitada') }}</th>
            <th class="col-centro">{{ $t('bandejaVacaciones.saldo') }}</th>
            <!-- #69 · la columna de la maqueta de «Anulaciones»: los días que se piden anular y el
                 motivo entrecomillado. Solo en esa pestaña: en las demás no hay petición que
                 enseñar y una columna vacía estrecha las que sí dicen algo. -->
            <th v-if="esAnulaciones">{{ $t('bandejaVacaciones.peticionAnulacion') }}</th>
            <th>{{ $t('bandejaVacaciones.trabajoPlanificado') }}</th>
            <th>{{ $t('bandejaVacaciones.acciones') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fila in filas" :key="fila.solicitud.id">
            <td>
              <div class="persona-nombre">{{ fila.solicitud.nombre || fila.solicitud.username }}</div>
              <!-- La función principal la trae la #51; mientras no esté desplegada el servidor
                   manda el rol, que es lo que sí existe hoy. -->
              <div class="persona-funcion">{{ fila.funcion || rolLegible(fila.rol) }}</div>
            </td>
            <td>
              <div class="linea-fechas">{{ fechasDe(fila.solicitud) }}</div>
              <span class="etiqueta" :class="'etiqueta-' + claveEstado(fila.solicitud.estado)">
                {{ $t('misVacaciones.estado_' + fila.solicitud.estado) }}
              </span>
              <!-- La marca que pidió dirección el 22/09: llegó su primer día sin que nadie la
                   resolviera (#62). Sigue PENDIENTE: no se ha aprobado ni denegado sola. -->
              <span v-if="fila.solicitud.vencidaSinResolver" class="etiqueta etiqueta-vencida">
                {{ $t('bandejaVacaciones.vencida') }}
              </span>
              <!-- #69 · la segunda etiqueta de la maqueta de «Anulaciones»: «Confirmada» (la de
                   arriba, que es su estado) más «Anulación pedida · N días». Son dos cosas a la
                   vez, y es el punto: la solicitud SIGUE confirmada mientras se decide. -->
              <span
                v-if="anulacionPendiente(fila.solicitud)"
                class="etiqueta etiqueta-anulacion"
              >
                {{ $t('bandejaVacaciones.anulacionPedida', {
                  n: anulacionPendiente(fila.solicitud).diasQuePide
                }) }}
              </span>
              <!-- Lo que quien pide los días escribió en «Comentario para quien aprueba». Se
                   enseña aquí, junto a las fechas y antes de los botones: es información que se
                   escribió expresamente para esta decisión y no se veía en ninguna parte. -->
              <div v-if="fila.solicitud.comentario" class="comentario-solicitante">
                {{ $t('bandejaVacaciones.comentarioSolicitante') }}: «{{ fila.solicitud.comentario }}»
              </div>
            </td>
            <td class="col-centro col-fuerte">{{ fila.solicitud.diasQueDescuenta }}</td>
            <td>{{ fechaHora(fila.solicitud.fechaCreacion, true) }}</td>
            <td class="col-centro">{{ saldoDe(fila) }}</td>
            <!-- #69 · qué días pide anular y por qué, entrecomillado como en la maqueta: es lo que
                 hay que leer para decidir, y sin ello el aprobador solo vería «pide anular 2». -->
            <td v-if="esAnulaciones" class="col-peticion">
              <template v-if="anulacionPendiente(fila.solicitud)">
                <div class="peticion-dias">
                  {{ diasPedidos(anulacionPendiente(fila.solicitud)) }}
                </div>
                <div class="peticion-motivo">
                  «{{ anulacionPendiente(fila.solicitud).motivo }}»
                </div>
                <div class="peticion-quien">
                  {{ $t('bandejaVacaciones.pedidaEl', {
                    fecha: fechaHora(anulacionPendiente(fila.solicitud).fecha, true)
                  }) }}
                </div>
              </template>
              <template v-else>—</template>
            </td>
            <td>
              <template v-if="fila.trabajoPlanificado && fila.trabajoPlanificado.length">
                <span class="chip-trabajo">
                  {{ $t('bandejaVacaciones.nTrabajos', { n: fila.trabajoPlanificado.length }) }}
                </span>
                <div v-for="(t, i) in fila.trabajoPlanificado" :key="'t' + i" class="trabajo-detalle">
                  {{ detalleTrabajo(t) }}
                </div>
              </template>
              <template v-else>—</template>
            </td>
            <td class="col-acciones">
              <!-- Una solicitud a una: no hay acciones en bloque, y es a propósito (la incidencia
                   lo dice así). Los botones se enseñan donde el SERVIDOR dice que se puede
                   resolver, no donde la pantalla lo deduzca del estado.

                   Y se apagan mientras la bandeja se está recargando: lo que hay en pantalla
                   todavía es la respuesta de ANTES de resolver —el año y la pestaña no han
                   cambiado, así que `bandejaVigente` la sigue dando por buena—, y el preloader se
                   apaga en cuanto contesta el servidor. Con una recarga lenta, la fila que se
                   acaba de aprobar seguía ahí con sus dos botones y se podía resolver otra vez,
                   para acabar en un conflicto de versión (revisión CODEX del PR #1735, ronda 1 de
                   la tanda del 06/10/2026).

                   El cerrojo de `unaAccionALaVez` es el tramo que faltaba, y es el de ANTES:
                   `cargando` empieza cuando el POST ha ido bien y el padre arranca la recarga, así
                   que entre aceptar el diálogo y esa respuesta los botones seguían encendidos.

                   Y es **de la solicitud**, no de esta pantalla: se pregunta por su clave
                   (`claveDeSolicitud`), la misma que usan el día amarillo del calendario y
                   «Anular» de «Mis vacaciones». Con el contador en el `data` del componente,
                   pasar a «Mis solicitudes» desmontaba esta bandeja y volver creaba otra con el
                   contador a cero: los botones se encendían sobre la solicitud que seguía
                   resolviéndose (revisión CODEX del PR #1735, ronda 3 de la tanda del 17/10/2026).

                   Apagado dice por qué (§9): la frase de debajo. -->
              <template v-if="fila.solicitud.puedeResolver">
                <f7-button
                  small
                  fill
                  color="green"
                  class="boton-fila"
                  :disabled="cargando || resolviendose(fila.solicitud)"
                  @click="aprobar(fila.solicitud)"
                >{{ $t('bandejaVacaciones.aprobar') }}</f7-button>
                <f7-button
                  small
                  color="red"
                  class="boton-fila"
                  :disabled="cargando || resolviendose(fila.solicitud)"
                  @click="denegar(fila.solicitud)"
                >{{ $t('bandejaVacaciones.denegar') }}</f7-button>
                <div v-if="resolviendose(fila.solicitud)" class="nota-resolviendo">
                  {{ $t('bandejaVacaciones.resolviendo') }}
                </div>
              </template>
              <!-- #69 · resolver la petición de anular. Los botones salen donde el SERVIDOR dice
                   (`puedeResolverAnulacion`), no donde la pantalla lo deduzca del estado: el
                   permiso no es un rol sino figurar como aprobador o suplente de esa persona
                   (D3). Son los dos de la maqueta, «Aceptar anulación» y «Rechazar».

                   Se apagan con el mismo cerrojo que «Aprobar» y «Denegar» —es la misma solicitud
                   y la misma resolución— y, apagados, lo dicen con la misma frase. -->
              <template v-if="fila.solicitud.puedeResolverAnulacion">
                <f7-button
                  small
                  fill
                  color="red"
                  class="boton-fila"
                  :disabled="cargando || resolviendose(fila.solicitud)"
                  @click="aceptarAnulacion(fila.solicitud)"
                >{{ $t('bandejaVacaciones.aceptarAnulacion') }}</f7-button>
                <f7-button
                  small
                  class="boton-fila"
                  :disabled="cargando || resolviendose(fila.solicitud)"
                  @click="rechazarAnulacion(fila.solicitud)"
                >{{ $t('bandejaVacaciones.rechazarAnulacion') }}</f7-button>
                <div v-if="resolviendose(fila.solicitud)" class="nota-resolviendo">
                  {{ $t('bandejaVacaciones.resolviendo') }}
                </div>
              </template>
              <!-- #69 · anular sin que nadie lo haya pedido (punto 3). Va en las pestañas donde
                   hay vacaciones confirmadas delante, que es donde tiene sentido. -->
              <f7-button
                v-if="fila.solicitud.puedeAnularDirectamente"
                small
                color="red"
                class="boton-fila"
                :disabled="cargando || resolviendose(fila.solicitud)"
                @click="$emit('anular', fila.solicitud)"
              >{{ $t('bandejaVacaciones.anularDirectamente') }}</f7-button>
              <!-- «Ver calendario» solo a quien puede entrar en el calendario. El permiso de esa
                   ruta no sale de aprobar a alguien: lo decide su menú, y un aprobador con rol
                   PRODUCCION puede no tenerlo. Enseñárselo era mandarle a un 403. Lo pregunta
                   `PermisosCalendario`, el mismo sitio al que pregunta la barrera de la ruta. -->
              <f7-button
                v-if="puedeAbrirCalendario"
                small
                class="boton-fila"
                @click="verCalendario(fila.solicitud)"
              >{{ $t('bandejaVacaciones.verCalendario') }}</f7-button>
            </td>
          </tr>
          <!-- Mientras se carga NO se dice que no hay nada: la pestaña o el año que se acaba de
               pedir todavía no han contestado, y «No tienes ninguna solicitud esperando» sería
               una respuesta que nadie ha dado. -->
          <tr v-if="!filas.length">
            <!-- Las columnas son una más en «Anulaciones» (la de la petición): con el 7 fijo, la
                 fila vacía dejaba un hueco a la derecha. -->
            <td :colspan="esAnulaciones ? 8 : 7" class="vacio">
              {{ cargando ? $t('bandejaVacaciones.cargando') : $t('bandejaVacaciones.vacia_' + pestana) }}
            </td>
          </tr>
        </tbody>
      </table>
    </f7-block>
  </div>
</template>
<style scoped>
.bandeja-subtitulo { font-size: 12px; color: #64748b; margin: 4px 16px 0; }
.tabla-wrapper { overflow-x: auto; }
.tabla-vacaciones { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-vacaciones th,
.tabla-vacaciones td {
  border-bottom: 1px solid #e0e0e0;
  padding: 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-vacaciones th { background-color: #f4f5f7; font-weight: bold; }
.tabla-vacaciones .col-centro { text-align: center; }
.tabla-vacaciones .col-fuerte { font-weight: bold; }
.persona-nombre { font-weight: bold; color: #1a2744; }
.persona-funcion { font-size: 11px; color: #8e8e93; }
.linea-fechas { font-weight: bold; color: #1a2744; }
/* El comentario puede ser una frase larga: aquí sí se parte en varias líneas, al contrario que
   las fechas, y se acota el ancho para que no estire la tabla entera. */
.comentario-solicitante {
  font-size: 11px;
  color: #64748b;
  font-style: italic;
  white-space: normal;
  max-width: 220px;
  margin-top: 2px;
}

/* Los mismos colores que los bordes del calendario y que las tarjetas de «Mis vacaciones»
   (D-UX): amarillo pendiente, azul confirmada, verde realizada. */
.etiqueta {
  display: inline-block;
  margin-top: 2px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  border: 1px solid #cfd4da;
  color: #64748b;
}
.etiqueta-solicitadas { border-color: #f0c419; color: #8a6d00; background-color: #fffbe6; }
.etiqueta-confirmadas { border-color: #2196f3; color: #11508c; background-color: #eef6ff; }
.etiqueta-disfrutadas { border-color: #4cd964; color: #1b7a34; background-color: #eefaf0; }
.etiqueta-vencida { border-color: #c0392b; color: #c0392b; background-color: #fdeceb; }
/* #69 · la anulación pedida. Naranja y no rojo: es una petición esperando respuesta, no un
   problema; el rojo de la tabla está reservado para lo que no debería estar pasando. */
.etiqueta-anulacion { border-color: #f0a000; color: #8a4b00; background-color: #fff4e5; }

.col-peticion { max-width: 240px; white-space: normal; }
.peticion-dias { font-size: 12px; font-weight: bold; color: #1a2744; }
.peticion-motivo { font-size: 11px; color: #64748b; font-style: italic; }
.peticion-quien { font-size: 11px; color: #8e8e93; }

.chip-trabajo {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  border: 1px solid #f0c419;
  background-color: #fffbe6;
  color: #8a6d00;
}
.trabajo-detalle { font-size: 11px; color: #8e8e93; }
.col-acciones { white-space: nowrap; }
.boton-fila { display: inline-flex; margin: 0 2px 2px 0; font-size: 12px; }
/* Va junto a los botones que explica, no en un aviso al principio de la pantalla (§9). */
.nota-resolviendo { font-size: 11px; color: #8a6d00; white-space: normal; max-width: 180px; }
.vacio { text-align: center; color: #8e8e93; padding: 16px; font-size: 13px; }
</style>
<script>
import RhBandejaVacaciones, { EVENTO_VACACIONES_RESUELTA } from "./../../js/db/RhBandejaVacaciones.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";
import { EventBus } from "./../../js/EventBus.js";
import textoDeLasFechas from "./../../js/utils/tramosSolicitud.js";
import accionDeLaSesion from "./../../js/utils/accionDeLaSesion.js";
import { unaAccionALaVez, claveDeSolicitud } from "./../../js/utils/unaAccionALaVez.js";
import PermisosCalendario from "./../calendario/permisos.js";

/**
 * La bandeja «Vacaciones · solicitudes de mi equipo» (#59): una pestaña de aprobación con sus
 * filas y sus acciones, incluidas las de la pestaña «Anulaciones» (#69).
 *
 * **Esta pantalla solo muestra y pulsa.** Quién puede aprobar qué, qué va en cada pestaña, el
 * saldo y el trabajo planificado los decide el servidor y llegan ya resueltos en una sola
 * petición. Aquí no se filtra por estado ni se cuenta nada: los botones salen donde
 * `puedeResolver` dice que sí, que es el mismo criterio con el que el servidor va a aceptar o
 * rechazar la acción.
 *
 * Es un componente y no una ruta propia porque vive dentro de la misma pantalla «Vacaciones» que
 * «Mis solicitudes» (#58): son pestañas de un mismo menú, no dos sitios distintos.
 */
export default {
  // Una resolución a la vez **por solicitud**: mientras su POST viaja, los botones de esa fila se
  // apagan, y los de las demás no. El cerrojo lo coge y lo suelta `accionDeLaSesion`, que es por
  // donde pasan los cuatro diálogos del módulo, y vive en el módulo de `unaAccionALaVez` y no en
  // esta instancia: desmontar la bandeja al cambiar de pestaña no lo suelta.
  mixins: [unaAccionALaVez],
  props: {
    /** La pestaña que se está enseñando: PENDIENTES, ANULACIONES, CONFIRMADAS o TODAS. */
    pestana: { type: String, required: true },
    /**
     * La respuesta entera del servidor para esa pestaña **y ese año**. La carga y recarga la
     * pantalla padre, que solo la pasa cuando describe la combinación que se está mirando: la
     * respuesta de la pestaña anterior no es una respuesta de esta.
     */
    datos: { type: Object, default: null },
    /** Si la petición de esta combinación está en vuelo. Decide qué dice la tabla vacía. */
    cargando: { type: Boolean, default: false }
  },
  computed: {
    filas: function() {
      return (this.datos && this.datos.solicitudes) || [];
    },
    /**
     * Si se le ofrece «Ver calendario». Aprobar a alguien **no** da acceso a la pantalla del
     * calendario: eso lo decide el menú de quien mira, y la barrera de la ruta lo comprueba con
     * `RoutesUtils.tieneAcceso`. Un aprobador con rol `PRODUCCION` —el caso que esta misma tarea
     * contempla— puede aprobar y no tener el calendario, y el botón le llevaba a un 403 sin que
     * la autorización del servidor llegara ni a ejecutarse (revisión CODEX del PR #1735).
     *
     * Se pregunta a `PermisosCalendario`, que es el único sitio donde está escrito: si el enlace
     * y la barrera lo resolvieran cada uno por su cuenta volverían a contestar cosas distintas.
     */
    puedeAbrirCalendario: function() {
      return PermisosCalendario.puedeAbrirCalendario();
    },
    /** #69 · si se está mirando «Anulaciones», que es la única pestaña con columna de petición. */
    esAnulaciones: function() {
      return this.pestana === 'ANULACIONES';
    },
    subtitulo: function() {
      if (!this.datos) return '';
      if (this.datos.todaLaPlantilla) return this.$t('bandejaVacaciones.subtituloTodos');
      const nombres = (this.datos.personas || [])
        .map(function(p) { return p.nombre || p.username; })
        .join(', ');
      return nombres
        ? this.$t('bandejaVacaciones.subtitulo', { nombres: nombres })
        : this.$t('bandejaVacaciones.subtituloNadie');
    }
  },
  methods: {
    /**
     * Si **esa** solicitud se está resolviendo ahora mismo, desde esta pantalla o desde cualquier
     * otra. Es lo que apaga sus dos botones y lo que enseña la frase que lo explica.
     */
    resolviendose: function(solicitud) {
      return this.accionEnVueloSobre(claveDeSolicitud(solicitud && solicitud.id));
    },
    /**
     * Aprobar. Se confirma antes porque confirma los días de otra persona en el calendario de
     * todo el mundo, y desde una tabla un clic despistado es fácil.
     *
     * El motivo **no se pide aquí**: aprobar no lo exige. Lo exige el semáforo de coincidencias
     * cuando sale en rojo (#64), y eso lo decide el servidor dentro de la propia transición: si
     * hace falta, contesta con el mensaje y la pantalla lo enseña tal cual.
     */
    aprobar: function(solicitud) {
      const self = this;
      // La acción se sella con la sesión de ahora ANTES de dibujar el diálogo, y no se envía con
      // otra. Y lleva dentro **sobre qué es**, que es de lo que se coge el cerrojo: con una
      // resolución de esa solicitud en vuelo no se abre otra —ni la de al lado, ni la contraria—,
      // y de eso responde `accionDeLaSesion` y no un `if` aquí, que era el que se olvidaba.
      const accion = accionDeLaSesion(self, claveDeSolicitud(solicitud.id));
      accion.confirmar(
        self.$t('bandejaVacaciones.confirmaAprobar', {
          quien: solicitud.nombre || solicitud.username, fechas: self.fechasDe(solicitud)
        }),
        self.$t('bandejaVacaciones.titulo'),
        function() {
          self.ejecutar(accion,
            RhBandejaVacaciones.aprobar(solicitud.id, solicitud.version, null),
            self.$t('bandejaVacaciones.aprobada'));
        }
      );
    },
    /**
     * Denegar. El motivo es **obligatorio** y por eso se pide con un `prompt` y no con un
     * `confirm`: es lo que va a leer quien pidió los días y lo único que explica por qué
     * desaparecen de su calendario.
     *
     * Vacío no vale, y se corta aquí para no gastar un viaje; pero quien manda es el servidor,
     * que lo exige igual (`RhSolicitudVacacionesService.denegar`).
     */
    denegar: function(solicitud) {
      const self = this;
      const app = self.$f7;
      const accion = accionDeLaSesion(self, claveDeSolicitud(solicitud.id));
      accion.pedir(
        self.$t('bandejaVacaciones.pideMotivo', {
          quien: solicitud.nombre || solicitud.username, fechas: self.fechasDe(solicitud)
        }),
        self.$t('bandejaVacaciones.titulo'),
        function(motivo) {
          if (!motivo || !motivo.trim()) {
            app.dialog.alert(self.$t('bandejaVacaciones.motivoObligatorio'));
            return;
          }
          self.ejecutar(accion,
            RhBandejaVacaciones.denegar(solicitud.id, solicitud.version, motivo.trim()),
            self.$t('bandejaVacaciones.denegada'));
        }
      );
    },
    /**
     * #69 · La petición de anular que está esperando respuesta, o `null`. Se mira el estado y no
     * solo que el objeto exista: una petición ya rechazada o caducada sigue ahí —es el rastro de
     * lo que pasó— y pintarla como pendiente ofrecería botones que el servidor rechaza.
     */
    anulacionPendiente: function(solicitud) {
      const p = solicitud && solicitud.peticionAnulacion;
      return p && p.estado === 'PENDIENTE' ? p : null;
    },
    /** «13 oct, 14 oct», los días concretos que se piden anular. */
    diasPedidos: function(peticion) {
      const self = this;
      const dias = (peticion && peticion.dias) || [];
      return dias.map(function(dia) { return self.fechaCorta(dia); }).join(', ');
    },
    /**
     * #69 · Aceptar la anulación. Se confirma antes y diciendo **cuántos días**: borra esos días de
     * la base de datos y no se deshace, y desde una tabla un clic despistado es fácil.
     *
     * El motivo no se pide: aceptar no lo exige —quien lo pidió ya escribió el suyo— y obligar a
     * escribir algo para decir «sí» solo produce motivos de relleno.
     */
    aceptarAnulacion: function(solicitud) {
      const self = this;
      const peticion = self.anulacionPendiente(solicitud);
      // El mismo sello y el mismo cerrojo que aprobar y denegar: resolver una anulación es una
      // resolución más de esa solicitud, y con una en vuelo no se abre otra (ni la contraria).
      const accion = accionDeLaSesion(self, claveDeSolicitud(solicitud.id));
      accion.confirmar(
        self.$t('bandejaVacaciones.confirmaAceptarAnulacion', {
          n: peticion ? peticion.diasQuePide : 0,
          quien: solicitud.nombre || solicitud.username,
          dias: self.diasPedidos(peticion)
        }),
        self.$t('bandejaVacaciones.titulo'),
        function() {
          self.ejecutar(accion,
            RhBandejaVacaciones.aceptarAnulacion(solicitud.id, solicitud.version, null),
            self.$t('bandejaVacaciones.anulacionAceptada'));
        }
      );
    },
    /**
     * #69 · Rechazarla. El motivo es **obligatorio** y por eso va con un `prompt` y no con un
     * `confirm`: es lo único que le explica a quien lo pidió por qué sigue teniendo esos días.
     *
     * Vacío no vale, y se corta aquí para no gastar un viaje; pero quien manda es el servidor, que
     * lo exige igual.
     */
    rechazarAnulacion: function(solicitud) {
      const self = this;
      const app = self.$f7;
      const accion = accionDeLaSesion(self, claveDeSolicitud(solicitud.id));
      accion.pedir(
        self.$t('bandejaVacaciones.pideMotivoAnulacion', {
          quien: solicitud.nombre || solicitud.username
        }),
        self.$t('bandejaVacaciones.titulo'),
        function(motivo) {
          if (!motivo || !motivo.trim()) {
            app.dialog.alert(self.$t('bandejaVacaciones.motivoObligatorio'));
            return;
          }
          self.ejecutar(accion,
            RhBandejaVacaciones.rechazarAnulacion(solicitud.id, solicitud.version, motivo.trim()),
            self.$t('bandejaVacaciones.anulacionRechazada'));
        }
      );
    },
    /**
     * El tronco común de las cuatro acciones —aprobar, denegar, y aceptar o rechazar una
     * anulación—: mismo preloader, mismo aviso y, sobre todo, **misma recarga**. Hay que recargar y
     * no tocar la fila en memoria: cualquiera de ellas cambia el saldo de esa persona, los
     * contadores de las cuatro pestañas y el badge del menú, y repintar solo la fila dejaría el
     * resto mintiendo. Aceptar una anulación además **saca la fila de «Anulaciones»** y cambia los
     * días de la solicitud, así que con más motivo.
     *
     * Recibe la `accion` y no la crea: el sello tiene que ser el del diálogo que empezó esto, no
     * uno nuevo tomado al enviar (que sería la sesión de ahora y daría todo por bueno).
     */
    ejecutar: function(accion, promesa, textoOk) {
      const self = this;
      const app = self.$f7;
      // #59 · Lo que pasa DESPUÉS de resolver es de la sesión que lo pidió, y la pregunta la
      // contesta el mismo sello que abrió el diálogo (`accionDeLaSesion`). Esta pantalla vive
      // dentro de una ruta con `keepAlive`: cerrar sesión y entrar con otra cuenta en la misma
      // pestaña no la recrea, así que el acuse de una aprobación ajena —y su recarga, y el badge—
      // aparecía sobre la pantalla de quien acababa de entrar. La acción en sí ya está hecha y
      // queda en `rh_audit`: lo que se descarta es contarla a quien no la ha pedido.
      //
      // Y por el mismo sitio se descarta lo que es de ESTA pantalla cuando ya no está delante:
      // salir de «Vacaciones» con el POST en vuelo no destruye nada —la ruta es `keepAlive`— y la
      // recarga del padre arrancaba igual, con su preloader, sobre la pantalla de destino.
      accion.enviar(promesa, {
        exito: function() {
          app.toast.create({ text: textoOk, position: 'center', closeTimeout: 2500 }).open();
          self.$emit('resuelta');
        },
        fallo: function(e) {
          // Si el adaptador ha cortado un intento repetido —o la respuesta es de una sesión que ya
          // se fue—, no hay nada que contar: la resolución que valía siguió su camino y el aviso
          // sería de algo que no ha pasado. Lo decide el adaptador y no este manejador, que es la
          // misma pregunta que ya hacía «Anular» (`sinNadaQueDecir`).
          if (RhBandejaVacaciones.sinNadaQueDecir(e)) return;
          app.dialog.alert(mensajeDelServidor(e, self.$t('bandejaVacaciones.errorResolviendo')));
        },
        // El badge del menú no cuelga de esta pantalla: el menú lateral se monta una vez en su
        // vista persistente y nadie lo vuelve a montar al navegar. Sin este aviso se quedaba con
        // el número de cuando se abrió la sesión. Va en `fuera` y no en `exito` justamente por eso:
        // si quien resolvió ya se ha ido de «Vacaciones», la recarga de esta pantalla sobra, pero
        // el número del menú que tiene delante no.
        fuera: function() {
          EventBus.$emit(EVENTO_VACACIONES_RESUELTA);
        }
      });
    },
    /**
     * «Ver calendario» abre el de esa persona. El servidor ya solo se lo deja ver a su aprobador.
     *
     * Quién puede entrar en la pantalla es otra pregunta, y la contesta `puedeAbrirCalendario`
     * antes de pintar el botón: aquí ya no se comprueba nada, porque no hay forma de llegar sin
     * ese botón.
     */
    verCalendario: function(solicitud) {
      this.$f7router.navigate('/calendario/' + encodeURIComponent(solicitud.username) + '/');
    },
    saldoDe: function(fila) {
      if (fila.saldoDisponible === null || fila.saldoDisponible === undefined) return '—';
      if (fila.saldoTotal === null || fila.saldoTotal === undefined) return String(fila.saldoDisponible);
      return fila.saldoDisponible + ' / ' + fila.saldoTotal;
    },
    /** «22 dic · RITE · CP Doctor Esquerdo», como en la maqueta. */
    detalleTrabajo: function(trabajo) {
      return [this.fechaCorta(trabajo.fecha), trabajo.trabajo, trabajo.cliente]
        .filter(function(t) { return !!t; })
        .join(' · ');
    },
    /** El rol se enseña con la primera en mayúscula, no gritado como lo guarda la ERP. */
    rolLegible: function(rol) {
      if (!rol) return '';
      const texto = String(rol).toLowerCase();
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
    claveEstado: function(estado) {
      if (estado === 'CONFIRMADA') return 'confirmadas';
      if (estado === 'REALIZADA') return 'disfrutadas';
      if (estado === 'PENDIENTE') return 'solicitadas';
      return 'cerradas';
    },
    /**
     * «21 dic – 30 dic», un solo día cuando empieza y acaba el mismo, y **los tramos uno a uno**
     * cuando la solicitud lleva varios: con los dos extremos, pedir 3–5 y 20–22 se enseñaba como
     * 3–22, tanto en la fila como en la confirmación de aprobar. Lo escribe `tramosSolicitud`,
     * que es el mismo sitio del que lo saca el calendario.
     */
    fechasDe: function(solicitud) {
      const self = this;
      return textoDeLasFechas(solicitud, function(iso) { return self.fechaCorta(iso); });
    },
    /**
     * «21 dic». Se compone a mano a partir del texto ISO y no se parsea a `Date`: un `LocalDate`
     * llega como «2026-12-21» y pasarlo por `Date` lo movería un día según la zona del navegador.
     * Es el mismo criterio que «Mis vacaciones».
     */
    fechaCorta: function(iso) {
      if (!iso) return '';
      const trozos = String(iso).substring(0, 10).split('-');
      if (trozos.length !== 3) return String(iso);
      return parseInt(trozos[2], 10) + ' ' + this.$t('misVacaciones.mes_' + parseInt(trozos[1], 10));
    },
    /**
     * Las marcas de tiempo sí son instantes (`Date` de Java), así que estas van por `Date` y se
     * enseñan en la zona del navegador, que es donde está quien lo lee.
     */
    fechaHora: function(valor, soloFecha) {
      if (!valor) return '';
      const d = new Date(valor);
      if (isNaN(d.getTime())) return String(valor);
      const dia = ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2)
        + '/' + d.getFullYear();
      if (soloFecha) return dia;
      return dia + ' ' + ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
    }
  }
};
</script>
